import { cloneElement, Component, createRef, Fragment, ReactNode } from 'react';

import ModalsContext, { CreateNoticeProps, CreateCustomModalNode, CreateModalProps } from './context';
import { BraxModal } from '../BraxModal';
import { BraxNotice } from '../BraxNotice';

type ModalsTypes = { type: 'modal', value: CreateModalProps } |
    { type: 'notice', value: CreateNoticeProps } |
    { type: 'custom', value: CreateCustomModalNode };

export type BraxModalsProps = {
    children: ReactNode
};

type BraxModalsState = {
    modals: (ModalsTypes | null)[]
};

export class BraxModals extends Component<BraxModalsProps, BraxModalsState> {

    state: BraxModalsState = {
        modals: []
    };

    ref = createRef<HTMLDivElement>();

    remove(index: number) {
        this.setState(prevState => {
            const modals = [...prevState.modals];

            const target = modals[index];

            if (index === undefined || target === null)
                return null;

            if (target.type === 'custom')
                target.value = cloneElement(target.value, { open: false });
            else
                target.value.open = false;

            setTimeout(() => {
                this.setState(prevState => {
                    const modals = [...prevState.modals];

                    modals[index] = null;

                    return { modals };
                });
            }, 500);

            modals[index] = target;

            return { modals };
        });
    }

    create({ onClose: _onClose, ...modal }: CreateModalProps) {
        let index: number;

        const onClose = () => {
            this.remove(index);

            if (_onClose)
                _onClose();
        };

        this.setState(prevState => {
            const modals = [...prevState.modals, {
                type: 'modal',
                value: { open: true, ...modal, onClose }
            } as ModalsTypes];

            index = modals.length - 1;

            return { modals };
        });

        return onClose;
    }

    notice(notice: CreateNoticeProps) {
        let index: number;

        const getClose = (callback?: Function) =>
            () => {
                this.remove(index);

                callback?.();
            };

        if (notice.type === 'confirm') {
            notice.onConfirm = getClose(notice.onConfirm);

            notice.onCancel = getClose(notice.onCancel);
        } else {
            notice.onClose = getClose(notice.onClose);
        }
    
        this.setState(prevState => {
            const modals = [...prevState.modals, {
                type: 'notice',
                value: { open: true, ...notice }
            } as ModalsTypes];

            index = modals.length - 1;

            return { modals };
        });

        return getClose();
    }

    custom(node: CreateCustomModalNode) {
        let index: number;

        const onClose = () => {
            this.remove(index);

            if (node.props.onClose)
                node.props.onClose();
        }

        this.setState(prevState => {
            const modals = [...prevState.modals, {
                type: 'custom',
                value: cloneElement(node, { open: true, onClose })
            } as ModalsTypes];

            index = modals.length - 1;

            return { modals };
        });

        return onClose;
    }

    render() {
        return (<>
            <ModalsContext.Provider value={{
                ref: this.ref,
                create: (modal: CreateModalProps) => this.create(modal),
                notice: (notice: CreateNoticeProps) => this.notice(notice),
                custom: (node: CreateCustomModalNode) => this.custom(node)
            }}>
                { this.props.children }

                { this.state.modals.map((modal, index) => modal === null
                    ? null
                : modal.type === 'modal'
                    ? <BraxModal key={index} {...modal.value} />
                : modal.type === 'notice'
                    ? <BraxNotice key={index} {...modal.value} />
                    : <Fragment key={index}>{ modal.value }</Fragment>
                ) }
            </ModalsContext.Provider>

            <div ref={this.ref} className="brax-modals" />
        </>);
    }

}
